import React from 'react';
import { Fragment, useState } from 'react';
import {
  Dialog,
  Popover,
  RadioGroup,
  Tab,
  Transition,
} from '@headlessui/react';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  PencilSquareIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, ClockIcon, StarIcon } from '@heroicons/react/20/solid';
import customFetch from '../../utils/customFetch';
import { Link, redirect, useLoaderData, useNavigate } from 'react-router-dom';
import { useCartContext } from '../../context/cartContext';
import ProductCustomizationSlideIn from './ProductCustomizationSlideIn';
import SEO from '../../utils/seo';

const policies = [
  {
    name: 'Keine monatlichen Kosten',
    description:
      'Wenn du eine sVisit Basic oder Custom Karte kaufst, bezahlst du nur einmal.',
    icon: CurrencyDollarIcon,
  },
  {
    name: 'Nur noch eine physische Karte',
    description: 'Du benötigst nur noch eine Visitenkarte: Deine sVisit-Karte.',
    icon: CreditCardIcon,
  },
  {
    name: 'Einfach anpassbar',
    description:
      'Haben sich deine Informationen geändert? Kein Problem, passe sie einfach online an.',
    icon: PencilSquareIcon,
  },
  {
    name: 'Höchste Sicherheit',
    description:
      'Deine Daten werden verschlüsselt übertragen und werden sicher gespeichert.',
    icon: LockClosedIcon,
  },
];
const reviews = {
  average: 4,
  totalCount: 1624,
  counts: [
    { rating: 5, count: 1019 },
    { rating: 4, count: 162 },
    { rating: 3, count: 97 },
    { rating: 2, count: 199 },
    { rating: 1, count: 147 },
  ],
  featured: [
    {
      id: 1,
      rating: 5,
      content: `
          <p>This is the bag of my dreams. I took it on my last vacation and was able to fit an absurd amount of snacks for the many long and hungry flights.</p>
        `,
      author: 'Emily Selman',
      avatarSrc:
        'https://images.unsplash.com/photo-1502685104226-ee32379fefbe?ixlib=rb-=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=8&w=256&h=256&q=80',
    },
    // More reviews...
  ],
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export const loader = async ({ params }) => {
  try {
    const { data } = await customFetch.get(`/products/${params.id}`);
    return data;
  } catch (error) {
    return redirect('/');
  }
};

const ProductDetail = () => {
  const data = useLoaderData();
  const product = data.product;
  const { addToCart } = useCartContext();
  const [showSlideIn, setShowSlideIn] = useState(false);

  const changeSlideIn = () => {
    setShowSlideIn(!showSlideIn);
  };

  // Weiterleiten, wenn zu Warenkorb hinzugefügt
  let navigate = useNavigate();
  const addAndGoToCart = () => {
    addToCart(product, 1, product.graphicFront, product.graphicBack);
    navigate('/warenkorb');
  };

  return (
    <>
      <SEO
        title={`${product.name} von sVisit - Die digitale Visitenkarte.`}
        description={`${product.description} Die digitale Visitenkarte mit NFC Chip.`}
        type="website"
        image={product.image}
      />
      <ProductCustomizationSlideIn
        showSlideIn={showSlideIn}
        changeSlideIn={changeSlideIn}
        product={product}
      />
      <main className="py-24">
        {/* Product */}
        <div className="bg-white">
          <div className="mx-auto max-w-2xl px-4 pb-24 pt-16 sm:px-6 sm:pb-32 sm:pt-24 lg:grid lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8">
            {/* Product details */}
            <div className="lg:max-w-lg lg:self-end">
              <nav aria-label="Breadcrumb">
                <ol role="list" className="flex items-center space-x-2">
                  <li>
                    <div className="flex items-center text-sm">
                      <a
                        href="/produkte"
                        className="font-medium text-gray-500 hover:text-gray-900"
                      >
                        Zurück zu allen Produkten
                      </a>
                    </div>
                  </li>
                </ol>
              </nav>

              <div className="mt-4">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  {product.name}
                </h1>
              </div>

              <section aria-labelledby="information-heading" className="mt-4">
                <h2 id="information-heading" className="sr-only">
                  Produktinformationen
                </h2>

                <div className="flex items-center">
                  <p className="text-lg text-gray-900 sm:text-xl">
                    CHF {product.price / 100}
                  </p>

                  {/* Reviews für später */}
                  {/* <div className="ml-4 border-l border-gray-300 pl-4">
                  <h2 className="sr-only">Reviews</h2>
                  <div className="flex items-center">
                    <div>
                      <div className="flex items-center">
                        {[0, 1, 2, 3, 4].map((rating) => (
                          <StarIcon
                            key={rating}
                            className={classNames(
                              reviews.average > rating
                                ? 'text-yellow-400'
                                : 'text-gray-300',
                              'h-5 w-5 shrink-0'
                            )}
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                      <p className="sr-only">
                        {reviews.average} out of 5 stars
                      </p>
                    </div>
                    <p className="ml-2 text-sm text-gray-500">
                      {reviews.totalCount} reviews
                    </p>
                  </div>
                </div> */}
                </div>

                <div className="mt-4 space-y-6">
                  <p className="text-base text-gray-500">
                    {product.description}
                  </p>
                </div>

                <div className="mt-6 flex items-center">
                  {product.inventory > 0 ? (
                    <>
                      <CheckIcon
                        className="h-5 w-5 shrink-0 text-green-500"
                        aria-hidden="true"
                      />
                      <p className="ml-2 text-sm text-gray-500">
                        Verfügbar und bereit zur Lieferung
                      </p>
                    </>
                  ) : (
                    <>
                      <ClockIcon
                        className="h-5 w-5 shrink-0 text-gray-300"
                        aria-hidden="true"
                      />
                      <p className="ml-2 text-sm text-gray-500">
                        Aktuell nicht auf Lager
                      </p>
                    </>
                  )}
                </div>
              </section>
            </div>

            {/* Product image */}
            <div className="mt-10 lg:col-start-2 lg:row-span-2 lg:mt-0 lg:self-center">
              <div className="aspect-h-1 aspect-w-1 overflow-hidden rounded-lg">
                <img
                  src={product.image}
                  alt={product.name}
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </div>

            {/* Product form */}
            <div className="mt-10 lg:col-start-1 lg:row-start-2 lg:max-w-lg lg:self-start">
              <section aria-labelledby="options-heading">
                {/* <h2 id="options-heading" className="sr-only">
                Product options
              </h2> */}

                <form>
                  <div className="sm:flex sm:justify-between">
                    {/* Size selector für später*/}
                    {/* <RadioGroup value={selectedSize} onChange={setSelectedSize}>
                    <RadioGroup.Label className="block text-sm font-medium text-gray-700">
                      Size
                    </RadioGroup.Label>
                    <div className="mt-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {product.sizes.map((size) => (
                        <RadioGroup.Option
                          as="div"
                          key={size.name}
                          value={size}
                          className={({ active }) =>
                            classNames(
                              active ? 'ring-2 ring-svisit-primary-500' : '',
                              'relative block cursor-pointer rounded-lg border border-gray-300 p-4 focus:outline-hidden'
                            )
                          }
                        >
                          {({ active, checked }) => (
                            <>
                              <RadioGroup.Label
                                as="p"
                                className="text-base font-medium text-gray-900"
                              >
                                {size.name}
                              </RadioGroup.Label>
                              <RadioGroup.Description
                                as="p"
                                className="mt-1 text-sm text-gray-500"
                              >
                                {size.description}
                              </RadioGroup.Description>
                              <div
                                className={classNames(
                                  active ? 'border' : 'border-2',
                                  checked
                                    ? 'border-svisit-primary-500'
                                    : 'border-transparent',
                                  'pointer-events-none absolute -inset-px rounded-lg'
                                )}
                                aria-hidden="true"
                              />
                            </>
                          )}
                        </RadioGroup.Option>
                      ))}
                    </div>
                  </RadioGroup> */}
                  </div>
                  <div className="mt-10">
                    {product.hasCustomization ? (
                      <a
                        href="#"
                        onClick={changeSlideIn}
                        className="flex w-full items-center justify-center rounded-md border border-transparent bg-svisit-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                      >
                        Individualisieren
                      </a>
                    ) : (
                      <Link
                        to="/warenkorb"
                        onClick={addAndGoToCart}
                        className="flex w-full items-center justify-center rounded-md border border-transparent bg-svisit-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                      >
                        Zum Warenkorb hinzufügen
                      </Link>
                    )}
                  </div>
                  <div className="mt-6 text-center">
                    <p className="group inline-flex text-base font-medium">
                      <ShieldCheckIcon
                        className="mr-2 h-6 w-6 shrink-0 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      <span className="text-gray-500 hover:text-gray-700">
                        Sichere Zahlungsabwicklung
                      </span>
                    </p>
                  </div>
                </form>
              </section>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6 sm:py-32 lg:max-w-7xl lg:px-8">
          {/* Details section */}
          <section aria-labelledby="details-heading">
            <div className="flex flex-col items-center text-center">
              <h2
                id="details-heading"
                className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
              >
                Was ist inklusive?
              </h2>
              <p className="mt-3 max-w-3xl text-lg text-gray-600">
                Wenn du deine Visitenkarte bei sVisit kaufst, erhälst du nicht
                einfach nur eine Karte: Du erhälst ausserdem den exklusiven
                Zugang zu deinem digitalen Visitenkarten-Profil, welches du
                vollständig nach deinen Vorstellungen gestalten kannst.
              </p>
            </div>

            <div className="mt-16 grid grid-cols-1 gap-y-16 lg:grid-cols-2 lg:gap-x-8">
              <div>
                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-lg">
                  <img
                    src="https://res.cloudinary.com/djvqy2bag/image/upload/v1708183654/sVisit_Basic_Card_min_e8y5gv.png"
                    alt="NFC Karte."
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <p className="mt-8 text-base text-gray-500">
                  Du benötigst nur noch eine einzige Visitenkarte: Deine
                  sVisit-Karte. Mit Ihr kannst du deine Informationen unendlich
                  oft weitergeben, ohne ständig physische Visitenkarten dabei zu
                  haben. Halte einfach deine sVisit-Karte an ein Handy oder
                  scanne deinen persönlichen QR-Code, um auf dein Profil zu
                  kommen.
                </p>
              </div>
              <div>
                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-lg">
                  <img
                    src="https://res.cloudinary.com/djvqy2bag/image/upload/v1699818232/Linkverwaltung_xybjna.png"
                    alt="Profilverwaltung."
                    className="h-full w-full object-contain object-center"
                  />
                </div>
                <p className="mt-8 text-base text-gray-500">
                  Organisiere dein sVisit-Profil nach deinen Wünschen. Bestimme,
                  welche Informationen du anzeigen möchtest, welche Farbe deine
                  Seite und deine Links haben sollen, und welche Bilder du
                  anzeigen möchtest.
                </p>
              </div>
            </div>
          </section>

          {/* Policies section */}
          <section aria-labelledby="policy-heading" className="mt-16 lg:mt-24">
            <h2 id="policy-heading" className="sr-only">
              Our policies
            </h2>
            <div className="grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-4 lg:gap-x-8">
              {policies.map((policy) => (
                <div key={policy.name}>
                  <policy.icon
                    className="h-24 w-auto text-svisit-primary-500"
                    aria-hidden="true"
                  />
                  <h3 className="mt-6 text-base font-medium text-gray-900">
                    {policy.name}
                  </h3>
                  <p className="mt-3 text-base text-gray-500">
                    {policy.description}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
        {/* Review section auf TailwindUI für später nachschauen */}
      </main>
    </>
  );
};

export default ProductDetail;
